import React, { useEffect, useState } from 'react';
import { CalendarHeart, PauseCircle, PlayCircle, Loader2, BadgeCheck } from 'lucide-react';
import client from '../../api/client';

const money = (cents, cur) => `${cur === 'cad' ? 'C$' : '$'}${((cents || 0) / 100).toFixed(cents % 100 ? 2 : 0)}`;
const day = (iso) => (iso ? new Date(iso).toLocaleDateString(undefined, { month: 'long', day: 'numeric', year: 'numeric' }) : '—');

export const useBillingSummary = () => {
  const [s, setS] = useState(null);
  useEffect(() => {
    client.get('/billing/summary').then(({ data }) => setS(data)).catch(() => {});
    const onChange = (e) => setS(e.detail);
    window.addEventListener('billing:changed', onChange);
    return () => window.removeEventListener('billing:changed', onChange);
  }, []);
  return [s, setS];
};

const CapMeter = ({ s }) => {
  const pct = Math.min(100, Math.round(((s.paid_cents || 0) / s.cap_cents) * 100));
  return (
    <div data-testid="billing-cap-meter">
      <div className="flex justify-between text-xs text-[#6A6A6A] mb-1">
        <span>Paid so far: <strong data-testid="billing-paid" className="text-[#2A2A2A]">{money(s.paid_cents, s.currency)}</strong> of {money(s.cap_cents, s.currency)}</span>
        <span>{s.status === 'paid_in_full' ? 'Paid in full' : `${money(s.remaining_cents, s.currency)} max to go`}</span>
      </div>
      <div className="h-2 rounded-full bg-[#F2E8DE] overflow-hidden">
        <div className="h-full bg-[#7B2C3B] transition-[width] duration-700" style={{ width: `${pct}%` }} />
      </div>
    </div>
  );
};

export const BillingCard = ({ toast }) => {
  const [s, setS] = useBillingSummary();
  const [busy, setBusy] = useState(false);
  const [months, setMonths] = useState(3);
  if (!s || (!s.has_subscription && !s.wedding_pass_ends_at)) return null;

  const act = async (path, body) => {
    setBusy(true);
    try {
      const { data } = await client.post(path, body);
      window.dispatchEvent(new CustomEvent('billing:changed', { detail: data }));
      toast?.({ title: data.paused ? 'Billing paused' : 'Billing resumed' });
    } catch (e) {
      toast?.({ title: 'Could not update billing', description: e.message, variant: 'destructive' });
    } finally { setBusy(false); }
  };

  if (!s.has_subscription) {
    return (
      <section data-testid="billing-card" className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5">
        <h3 className="font-serif text-xl text-[#2A2A2A]">Wedding Pass</h3>
        <p className="text-sm text-[#6A6A6A] mt-1">Paid once. Covered through {day(s.wedding_pass_ends_at)}. Nothing to cancel.</p>
      </section>
    );
  }

  return (
    <section data-testid="billing-card" className="soft-card rounded-2xl p-6 bg-white border border-[#EFE3D8] mb-5 space-y-4">
      <div className="flex items-start justify-between gap-3 flex-wrap">
        <div>
          <h3 className="font-serif text-xl text-[#2A2A2A] capitalize">{s.tier} plan</h3>
          <p className="text-xs text-[#6A6A6A]">{money(s.recurring_cents, s.currency)}/{s.cadence === 'year' ? 'yr' : 'mo'}</p>
        </div>
        {s.status === 'paid_in_full' && <span data-testid="billing-paid-in-full" className="inline-flex items-center gap-1 text-xs px-3 py-1 rounded-full bg-[#EAF2E5] text-[#2F5D2F]"><BadgeCheck className="w-3.5 h-3.5" /> Paid in full</span>}
        {s.paused && <span data-testid="billing-paused-badge" className="text-xs px-3 py-1 rounded-full bg-[#FBF1EA] text-[#A15A2A]">Paused until {day(s.resumes_at)}</span>}
      </div>
      {s.cap_cents && <CapMeter s={s} />}
      <p data-testid="billing-ends-at" className="text-sm text-[#3A3A3A] flex items-start gap-2">
        <CalendarHeart className="w-4 h-4 text-[#7B2C3B] mt-0.5 flex-shrink-0" />
        {s.status === 'paid_in_full'
          ? <>No more charges. Your plan stays on until {day(s.ends_at)}.</>
          : <>Ends automatically on <strong>{day(s.ends_at)}</strong> {s.ends_reason === 'wedding' ? '(90 days after your wedding)' : '(18 months after signup, since no wedding date is set; add your date to update it)'}. No need to cancel.</>}
      </p>
      {s.status === 'active' && (s.paused ? (
        <div className="rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#6A6A6A]">Your wedding is view-only while paused. Billing restarts on its own on {day(s.resumes_at)}.</p>
          <button data-testid="billing-resume" disabled={busy} onClick={() => act('/billing/resume')} className="text-xs px-4 py-2 rounded-full bg-[#7B2C3B] text-white inline-flex items-center gap-1.5 disabled:opacity-50">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PlayCircle className="w-3.5 h-3.5" />} Resume now
          </button>
        </div>
      ) : (
        <div className="rounded-xl bg-[#FBF7F2] border border-[#EFE3D8] p-3 flex items-center justify-between gap-3 flex-wrap">
          <p className="text-xs text-[#6A6A6A] max-w-md">Quiet stretch in your planning? Pause billing for up to 3 months. Everything stays saved and visible; sends and edits wait until you resume.</p>
          <div className="flex items-center gap-2">
            <select data-testid="billing-pause-months" value={months} onChange={(e) => setMonths(Number(e.target.value))} className="text-xs px-3 py-2 rounded-full border border-[#EFE3D8] bg-white">
              {[1, 2, 3].map((m) => <option key={m} value={m}>{m} month{m > 1 ? 's' : ''}</option>)}
            </select>
            <button data-testid="billing-pause" disabled={busy} onClick={() => act('/billing/pause', { months })} className="text-xs px-4 py-2 rounded-full border border-[#7B2C3B] text-[#7B2C3B] inline-flex items-center gap-1.5 hover:bg-[#7B2C3B] hover:text-white transition-colors disabled:opacity-50">
              {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <PauseCircle className="w-3.5 h-3.5" />} Pause billing
            </button>
          </div>
        </div>
      ))}
    </section>
  );
};

export const BillingPausedBanner = ({ onOpenSettings }) => {
  const [s] = useBillingSummary();
  if (!s?.paused) return null;
  return (
    <div data-testid="billing-paused-banner" className="mb-6 rounded-2xl px-5 py-3 bg-[#FBF1EA] border border-[#EFC9B0] flex items-center justify-between gap-3 flex-wrap">
      <p className="text-sm text-[#7A3E1A]">Billing is paused. Your wedding is view-only until {day(s.resumes_at)}.</p>
      <button data-testid="billing-paused-banner-open" onClick={onOpenSettings} className="text-xs px-4 py-1.5 rounded-full bg-[#7B2C3B] text-white">Resume</button>
    </div>
  );
};

export default BillingCard;
